"use client";
import React, { useContext, type FC } from "react";
import InvitationQRCode from "./InvitationQRCode";
import { InvitationAvatar, InvitationOwnerAvatar } from "./InvitationAvatars";
import { WEBSITE_URL } from "~/src/utils/common";
import { PartyInvitationCardContext, Skeleton } from "./context";
import AvacusLogoLink from "../AvacusLogoLink";
import { Body2B, Body2R, H6, Sub2 } from "../Common/Text";
import { useTranslations } from "next-intl";

const InvitationCardMobile: FC<{ qrCodeURL: string; loadingQr?: boolean }> = ({
  qrCodeURL,
  loadingQr = false,
}) => {
  const t = useTranslations();

  const {
    name = "",
    ownerName = "",
    members = 0,
    isRendering = true,
  } = useContext(PartyInvitationCardContext);

  return (
    <div className="flex w-full min-w-0 flex-col justify-center items-center rounded-xl gap-3">
      <div className="flex w-full min-w-0 flex-col justify-center items-center gap-3 md:gap-6">
        <div className="flex justify-center items-center">
          <AvacusLogoLink />
        </div>
        <div className="w-full max-w-60 md:max-w-75 border border-primary rounded-2xl p-3 bg-white">
          <InvitationQRCode value={qrCodeURL} loading={loadingQr} compact />
        </div>
        <div className="flex flex-col text-subtle-light w-full gap-3 justify-center items-center">
          <div className="size-15 shrink-0">
            <InvitationAvatar compact />
          </div>
          <div className="relative flex h-6 w-full min-w-0 flex-row items-center justify-center gap-2">
            <img
              src={`${WEBSITE_URL}/static/confetti-icon.svg`}
              alt="Confetti"
              loading="lazy"
              decoding="async"
              className="size-6 shrink-0 object-contain"
            />
            {isRendering ? (
              <Skeleton
                className="border border-primary"
                width={130}
                height={24}
                borderRadius={10}
              />
            ) : (
              <H6 className="min-w-0 truncate text-primary">
                {name?.length > 16
                  ? name.substring(0, 15) + "..."
                  : name || t("party_chat")}
              </H6>
            )}
          </div>
          <div className="flex justify-between items-center w-full border border-primary rounded-2xl px-4 bg-white h-10">
            <Body2R>{t("web.members")}:</Body2R>
            {isRendering ? <Skeleton width={48} /> : <Body2B>{members}</Body2B>}
          </div>
          <div className="flex justify-between items-center w-full border border-primary rounded-2xl px-4 bg-white h-10 gap-2">
            <Body2R>{t("owner")}:</Body2R>

            {isRendering ? (
              <div className="flex justify-end items-center gap-2">
                <Skeleton width={24} height={24} borderRadius={24} />
                <Skeleton width={64} />
              </div>
            ) : (
              <div className="flex flex-1 justify-end items-center overflow-hidden">
                <div className="w-fit flex justify-center items-center">
                  <InvitationOwnerAvatar compact />
                </div>
                <Body2B className="ml-2 break-all line-clamp-1 max-w-37.5">
                  {ownerName}
                </Body2B>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-full border rounded-xl border-blue-info flex justify-center items-center bg-blue-2 py-2 px-2 gap-2 mt-2">
        <img
          src={`${WEBSITE_URL}/static/ic-scan-blue.svg`}
          alt="Scan"
          loading="lazy"
          decoding="async"
          className="size-5 object-contain"
        />
        <Sub2 className="text-blue-info text-center min-w-0">
          {t("web.scan_qr_code_on_avacus_app")}
        </Sub2>
      </div>
    </div>
  );
};

export default InvitationCardMobile;
